import {ActionTree} from 'vuex';
import axios, { AxiosResponse } from 'axios';
import {RootState} from '@/store/types';
import {
  CallDetailEventsFilterParams,
  CallDetailItem,
  CallGroupFilterParams,
  CallGroupIncomingFilterParams,
  CallGroupIncomingItem,
  CallGroupIncomingSummary,
  CallGroupItem,
  CallTemplateFileUpload,
  DetailFilterParams,
  DetailIncomingFilterParams,
  StatisticState,
  CallGroupSummary
} from './types';

export const actions: ActionTree<StatisticState, RootState> = {
  async getCallStatisticDetail({commit}, params: DetailFilterParams): Promise<AxiosResponse> {
    commit('setLoading', true);
    try {
      const response: AxiosResponse<{messages: CallDetailItem[]}> = await axios({url: '/stats-api/detail/call', params: params});
      let messages = response.data?.messages ?? [];
      if (params.page_number > 1) {
        commit('pushCallStatisticDetail', messages);
      } else {
        commit('setCallStatisticDetail', messages);
      }
      return response;
    } catch (error) {
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  async exportCallStatisticDetail({commit}, params: DetailFilterParams): Promise<AxiosResponse<{status: string}>> {
    commit('setLoading', true);
    try {
      const response: AxiosResponse<{status: string}> = await axios({
        url: '/stats-api/detail/call/export',
        params: params,
      });
      return response;
    } catch (error) {
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  getCallStatisticDetailInfo: ({ commit }, id: string) => {
    commit("setLoading", true);
    return new Promise((resolve, reject) => {
      axios({
        url: "/stats-api/detail/call/" + id.replace(/#/g, "%23"),
      })
        .then((resp) => {
          let messageItem = resp.data;
          resolve(messageItem);
        })
        .catch((err) => {
          reject(err);
        })
        .finally(() => commit("setLoading", false));
    });
  },

  async resendCallMessage({commit}, data: any) {
    commit('setLoading', true);
    try {
      const resp: AxiosResponse<{status: string}> = await axios({
        method: 'post',
        url: `/acapi/tools/call/resend`,
        data: data
      });
      return resp;
    } catch (err) {
      throw err;
    } finally {
      commit('setLoading', false);
    }
  },

    async getCallDetailEvents({commit}, params: { id: string, filter: CallDetailEventsFilterParams }) {
     commit('setLoading', true);
     try {
        const resp = await axios({
          url: `/stats-api/detail/call/${encodeURIComponent(params.id)}/events`,
          params: params.filter,
     });
        commit("setCallDetailEvents", resp.data.events);
        return resp;
     } catch (err) {
        throw err;
     } finally {
        commit('setLoading', false);
     }
  },

  async getCallStatisticGroup({commit}, params: CallGroupFilterParams): Promise<AxiosResponse> {
    commit('setLoading', true);
    try {
      const resp: AxiosResponse<{messages_summary?: CallGroupSummary, messages: CallGroupItem[]}> = await axios({
        url: '/stats-api/group/call',
        params: params,
      });
      const messages = (resp.data.messages ?? []).map((item: CallGroupItem) => ({...item, error: item.failed ?? 0}));
      const summary = resp.data.messages_summary ? {...resp.data.messages_summary, error: resp.data.messages_summary.failed ?? 0} : undefined;
      commit('setCallStatisticGroup', messages);
      commit('setCallStatisticGroupSummary', summary);
      return resp;
    } catch (err) {
      throw err;
    } finally {
      commit('setLoading', false);
    }
  },

  async exportCallStatisticGroup({commit}, params: CallGroupFilterParams) {
    commit('setLoading', true);
    try {
      const response: AxiosResponse<{status: string}> = await axios({
        url: '/stats-api/group/call/export',
        params: params,
      });
      return response;
    } catch (error) {
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  async getCallStatisticGroupIncoming({commit}, filterParams: CallGroupIncomingFilterParams) {
    commit('setLoading', true);
    try {
      let response: AxiosResponse<{messages_count: number, messages: CallGroupIncomingItem[], messages_summary?: CallGroupIncomingSummary}> = await axios({
        url: '/stats-api/group/call',
        params: filterParams,
      });
      commit('setCallStatisticGroupIncoming', response.data.messages ?? []);
      commit('setCallStatisticGroupIncomingSummary', response.data.messages_summary);
      commit('setCallIncomingMessagesCount', response.data.messages_count);
      return response;
    } catch (error) {
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  async exportCallStatisticGroupIncoming({commit}, filterParams: CallGroupIncomingFilterParams) {
    commit('setLoading', true);
    try {
      let response: AxiosResponse<{status: string}> = await axios({
        url: '/stats-api/group/call/export',
        params: filterParams,
      });
      return response;
    } catch (error) {
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  async getCallStatisticDetailIncoming({commit}, params: DetailIncomingFilterParams) {
    commit('setLoading', true);
    try {
      let response = await axios({
        url: '/stats-api/detail/call',
        params: params,
      });
      let messages = response.data?.messages ?? [];
      if (params.page_number > 1) {
        commit('pushCallStatisticDetailIncoming', messages);
      } else {
        commit('setCallStatisticDetailIncoming', messages);
      }
      return response;
    } catch (error) {
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  async getCallStatisticDetailIncomingInfo({commit}, id: string) {
    commit('setLoading', true);
    try {
      const response = await axios({
        url: '/stats-api/detail/call/' + id.replace(/#/g, '%23'),
      });
      const data = response.data;
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  async exportCallStatisticDetailIncoming({commit}, params: DetailIncomingFilterParams) {
    commit('setLoading', true);
    try {
      let response = await axios({
        url: '/stats-api/detail/call/export',
        params: params,
      });
      return response;
    } catch (error) {
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  async uploadCallTemplateFile({commit}, data: CallTemplateFileUpload) {
    commit('setLoading', true);
    try {
      const response = await axios({
        method: 'post',
        url: '/acapi/call/templates/files',
        data: data,
      });
      return response;
    } finally {
      commit('setLoading', false);
    }
  }
};
